import NakedDropdown from '@app/components/inputs/NakedDropdown';
import Toggle from '@app/components/inputs/Toggle';
import SVG from '@app/components/SVG';
import {
  convertValidatedAccountValidationSupplierValidationLevelToText,
  ValidatedPayeeAccountValidationStatusSupplierValidationVerification,
} from '@app/domain/validatedPayeeAccount';
import CopyIcon from '@app/images/copy.svg';
import TrashIcon from '@app/images/trash-black.svg';
import React, { FC, ReactElement } from 'react';
import { VALIDATION_TYPE_OPTIONS } from '../NewVerificationTab.constants';
import { AccountSection as AccountSectionType, TableColumn } from '../NewVerificationTab.data';
import {
  AccountControls,
  AccountControlsLeft,
  AccountControlsRight,
  AccountDetailsHeader,
  AccountDetailsSection,
  AccountHeaderActions,
  ControlGroup,
  IconButton,
  Label,
  OriginallyLyonsNote,
  PrivateAccountRow,
  SectionHeader,
  ValidationTypeDropdownItem,
} from '../NewVerificationTab.styles';
import { GenericTable } from './GenericTable';

import Loadable from '@app/utils/Loadable';
import { LegalEntityIdType } from '@mortee/domain/vaildatedPayeeManagement';

interface AccountSectionProps {
  section: AccountSectionType;
  sectionIndex: number;
  totalSections: number;
  columns: TableColumn[];
  taxIdTypes?: Loadable<LegalEntityIdType[]>;
  /** Country codes for which a type (by its id) is configured as a Semi-Strong LEI. */
  semiStrongCountryCodesByTypeId?: Record<string, string[]>;
  canEditManualProcess?: boolean;
  canEditFinalReview?: boolean;
  /** When the record was originally validated in Lyons and has additional evidence on top of it. */
  showOriginallyLyonsNote?: boolean;
  onValidationTypeChange: (
    sectionId: string,
    value: ValidatedPayeeAccountValidationStatusSupplierValidationVerification | undefined,
  ) => void;
  onPrivateAccountChange: (sectionId: string, value: boolean) => void;
  onDuplicate: (sectionId: string) => void;
  onDelete: (sectionId: string) => void;
  onRowUpdate: (sectionId: string, rowIndex: number, columnKey: string, newValue: React.ReactNode) => void;
  onAddSubRow: (sectionId: string, rowIndex: number) => void;
  onDeleteRow: (sectionId: string, rowIndex: number) => void;
}

export const AccountSection: FC<AccountSectionProps> = ({
  section,
  sectionIndex,
  totalSections,
  columns,
  taxIdTypes,
  semiStrongCountryCodesByTypeId,
  canEditManualProcess,
  canEditFinalReview,
  showOriginallyLyonsNote,
  onValidationTypeChange,
  onPrivateAccountChange,
  onDuplicate,
  onDelete,
  onRowUpdate,
  onAddSubRow,
  onDeleteRow,
}) => {
  const isValidationTypeLyons =
    section.validationType === ValidatedPayeeAccountValidationStatusSupplierValidationVerification.lyonsValidation;

  const renderValidationTypeDropdown = (): ReactElement => {
    return (
      <NakedDropdown
        accessibilityLabel='Account Validation Type'
        name={`validation-type-${section.id}`}
        dataTestId={`validation-type-${section.id}`}
        placeholder='Validation Type'
        value={section.validationType}
        onChange={(value): void => {
          const newValidationType = value as ValidatedPayeeAccountValidationStatusSupplierValidationVerification;
          onValidationTypeChange(section.id, newValidationType);
          // If Lyons validation, set private account to true
          if (newValidationType === ValidatedPayeeAccountValidationStatusSupplierValidationVerification.lyonsValidation) {
            onPrivateAccountChange(section.id, true);
          }
        }}
      >
        {VALIDATION_TYPE_OPTIONS.map((validationTypeOption) => {
          const validationTypeText = convertValidatedAccountValidationSupplierValidationLevelToText(validationTypeOption);
          return (
            <ValidationTypeDropdownItem
              key={validationTypeOption}
              value={validationTypeOption}
              textWhenSelected={validationTypeText}
            >
              <div>{validationTypeText}</div>
            </ValidationTypeDropdownItem>
          );
        })}
      </NakedDropdown>
    );
  };

  return (
    <AccountDetailsSection>
      <AccountDetailsHeader>
        <SectionHeader>Account Details {totalSections > 1 ? `(${sectionIndex + 1})` : ''}</SectionHeader>
        <AccountHeaderActions>
          <IconButton onClick={(): void => onDuplicate(section.id)}>
            <SVG accessibilityLabel='Copy' image={CopyIcon} width={14} height={14} />
          </IconButton>
          <IconButton onClick={(): void => onDelete(section.id)}>
            <SVG accessibilityLabel='Delete' image={TrashIcon} width={14} height={14} />
          </IconButton>
        </AccountHeaderActions>
      </AccountDetailsHeader>
      <AccountControls>
        <AccountControlsLeft>
          <ControlGroup>
            {renderValidationTypeDropdown()}
            {showOriginallyLyonsNote && (
              <OriginallyLyonsNote data-testid={`originally-lyons-note-${section.id}`}>
                The registered account was originally validated in Lyons.
              </OriginallyLyonsNote>
            )}
          </ControlGroup>
        </AccountControlsLeft>
        <AccountControlsRight>
          <PrivateAccountRow>
            <Label>Private Account</Label>
            <Toggle
              accessibilityLabel='Private Account toggle'
              name={`private-account-${section.id}`}
              value={section.privateAccount}
              onChange={(value): void => onPrivateAccountChange(section.id, value ?? false)}
              disabled={isValidationTypeLyons}
            />
          </PrivateAccountRow>
        </AccountControlsRight>
      </AccountControls>
      <GenericTable
        columns={columns}
        rows={section.rows}
        taxIdTypes={taxIdTypes}
        semiStrongCountryCodesByTypeId={semiStrongCountryCodesByTypeId}
        canEditManualProcess={canEditManualProcess}
        canEditFinalReview={canEditFinalReview}
        onRowUpdate={(rowIndex, columnKey, newValue): void => {
          onRowUpdate(section.id, rowIndex, columnKey, newValue);
        }}
        onAddSubRow={(rowIndex): void => {
          onAddSubRow(section.id, rowIndex);
        }}
        onDeleteRow={(rowIndex): void => {
          onDeleteRow(section.id, rowIndex);
        }}
      />
    </AccountDetailsSection>
  );
};
